import { CASES } from "@/content/cases";
import { ChannelVisualizer } from "@/components/home/ChannelVisualizer";
import { Link } from "@tanstack/react-router";
import { ParticleHero } from "@/components/ParticleHero";
import { Reveal } from "@/components/Reveal";
import { LOGOS } from "@/components/home/home-content";
import { ArrowUpRight, Sparkles, TrendingUp, Search, Bot } from "lucide-react";

export function HomeHero() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <ParticleHero glowStrength={1.4} brightnessThreshold={150} verticalOffset={0.22} horizontalOffset={0.28} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/40 to-background -z-10 pointer-events-none" />
        <div className="pointer-events-none absolute -top-32 -left-32 size-[28rem] rounded-full bg-primary/20 blur-3xl animate-blob -z-10" />
        <div className="pointer-events-none absolute top-1/3 -right-40 size-[32rem] rounded-full bg-accent/15 blur-3xl animate-blob -z-10" style={{ animationDelay: "-6s" }} />

        <div className="container-x pt-20 pb-32 lg:pt-28 lg:pb-44 relative">
          <Reveal>
            <div
              className="lift-on-hover hover:-translate-y-0.5 inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              SEO · AEO · GEO · Digital Marketing — Rangpur, Bangladesh
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h1 className="mt-8 font-display text-[14vw] md:text-[10vw] lg:text-[8.5rem] leading-[0.92] tracking-[-0.04em] max-w-6xl">
              <em className="not-italic text-gradient">Rank higher.</em>
              <br />
              Get cited by AI.
              <br />
              <span className="text-muted-foreground/70">Grow forever.</span>
            </h1>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-8 font-display text-2xl md:text-3xl leading-snug max-w-3xl">
              Boost your brand with <span className="text-gradient">Tamal's hand</span> — marketing strategies that stand.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <p className="mt-10 max-w-xl text-lg text-muted-foreground leading-relaxed">
              I'm Tamal — a senior SEO, AEO, GEO and digital marketing expert
              from Rangpur, Bangladesh, engineering organic visibility across
              Google, ChatGPT, Perplexity, Gemini and the engines that come
              next. I work on technical SEO, useful content and measurement
              for founders, agencies and ecommerce teams — with priorities
              based on their own search data.
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap gap-3">
              <div className="pop-on-hover">
                <Link
                  to="/contact"
                  className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition-all"
                >
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full transition-transform duration-700" />
                  Start a project
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </Link>
              </div>
              <div className="pop-on-hover">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-card hover:border-primary/40 transition"
                >
                  See projects & approaches
                </Link>
              </div>
            </div>
          </Reveal>

          {/* 4-pillar strip */}
          <Reveal delay={0.5}>
            <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { to: "/services", hash: "seo", icon: Search, label: "SEO", desc: "Improve visibility for the keywords your buyers search.", cta: "Explore SEO services" },
                { to: "/services/aeo", icon: Bot, label: "AEO", desc: "Get cited by ChatGPT, Perplexity, Claude & Gemini.", cta: "Read about AEO" },
                { to: "/services/geo", icon: Sparkles, label: "GEO", desc: "Win Google AI Overviews & generative search.", cta: "See GEO approach" },
                { to: "/services/digital-marketing", icon: TrendingUp, label: "Digital Marketing", desc: "Full-funnel strategy that compounds organically.", cta: "View digital marketing" },
              ].map((p) => (
                <Link
                  key={p.label}
                  to={p.to}
                  hash={p.hash}
                  className="group rounded-2xl border border-border bg-card/60 p-5 hover:border-primary/50 transition"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <p.icon size={18} />
                    <span className="text-xs uppercase tracking-[0.2em]">{p.label}</span>
                  </div>
                  <p className="mt-3 text-sm text-foreground/90 leading-snug">{p.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
                    {p.cta} <ArrowUpRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>

          {/* Interactive channel visualizer */}
          <Reveal delay={0.6}>
            <div className="mt-20">
              <ChannelVisualizer />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative py-10 border-y border-border overflow-hidden">
        <div className="container-x">
          <div className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
            Selected client projects
          </div>
        </div>
        <div className="flex gap-12 md:gap-16 marquee whitespace-nowrap">
          {(() => { const real = CASES.filter((c) => !/example/i.test(c.client)); return [...real, ...real, ...real, ...real]; })().map((c, i) => (
            <span key={`c-${i}`} className="font-display text-xl md:text-2xl text-foreground/80">
              {c.client.replace(/\s*\(.*?\)\s*/g, "")}
            </span>
          ))}
        </div>
        <div className="flex gap-12 md:gap-16 marquee whitespace-nowrap mt-6 opacity-60" style={{ animationDirection: "reverse" }}>
          {[...LOGOS, ...LOGOS, ...LOGOS].map((l, i) => (
            <span key={`t-${i}`} className="font-display text-base md:text-lg text-muted-foreground/70">{l}</span>
          ))}
        </div>
      </div>
    </>
  );
}
